import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Pencil, Download, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/public`;

export default function PublicBudget() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showSign, setShowSign] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/budgets/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "No encontrado"));
  };
  useEffect(load, [token]);

  if (error) return <Centered><div className="text-center"><X className="w-12 h-12 text-red-500 mx-auto" /><h2 className="font-display font-extrabold text-2xl mt-4">{error}</h2></div></Centered>;
  if (!data) return <Centered><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></Centered>;

  const { budget, owner } = data;
  const brand = owner.brand_color || "#EA580C";
  const closed = budget.status === "accepted" || budget.status === "rejected";

  const accept = async () => {
    setBusy(true);
    try { await axios.post(`${API}/budgets/${token}/accept`); toast.success("Presupuesto aceptado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const reject = async () => {
    if (!window.confirm("¿Seguro que quieres rechazar este presupuesto?")) return;
    setBusy(true);
    try { await axios.post(`${API}/budgets/${token}/reject`); toast.info("Presupuesto rechazado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const sign = async (payload) => {
    setBusy(true);
    try {
      await axios.post(`${API}/budgets/${token}/sign`, payload);
      toast.success("Presupuesto firmado");
      setShowSign(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error firmando"); }
    finally { setBusy(false); }
  };
  const downloadPdf = () => window.open(`${API}/budgets/${token}/pdf`, "_blank");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-budget-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            {owner.logo_url ? (
              <img src={`${BACKEND}${owner.logo_url}`} alt={owner.business_name} className="h-12 max-w-[160px] object-contain" data-testid="public-logo" />
            ) : (
              <div className="font-display font-extrabold text-xl">{owner.business_name}</div>
            )}
            <div className="text-sm">
              <div className="font-bold">{owner.business_name}</div>
              <div className="text-xs text-slate-500">{owner.email} · {owner.phone}</div>
            </div>
          </div>
          <button onClick={downloadPdf} data-testid="public-download-pdf" className="h-10 px-4 border border-slate-200 hover:border-slate-300 inline-flex items-center gap-2 text-sm font-semibold"><Download className="w-4 h-4" /> Descargar PDF</button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10 space-y-6">
        {/* Title */}
        <div className="bg-white border border-slate-200 p-8">
          <div className="kbar" style={{ color: brand }}>Presupuesto · {budget.number}</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-2">{budget.title}</h1>
          {budget.summary && <p className="text-slate-600 mt-3 text-base">{budget.summary}</p>}

          {/* Status badge */}
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <StatusBadge status={budget.status} />
            {budget.has_signature && (
              <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 bg-blue-100 text-blue-700">✍ Firmado por {budget.signer_name}</span>
            )}
            {budget.accepted_at && <span className="text-xs text-slate-400">Aceptado el {new Date(budget.accepted_at).toLocaleString("es-ES")}</span>}
            {budget.rejected_at && <span className="text-xs text-slate-400">Rechazado el {new Date(budget.rejected_at).toLocaleString("es-ES")}</span>}
          </div>
        </div>

        {/* Client */}
        <Section label="Cliente">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <Field icon={Pencil} label="Nombre" v={budget.client_name} />
            {budget.client_phone && <Field icon={Phone} label="Teléfono" v={budget.client_phone} />}
            {budget.client_email && <Field icon={Mail} label="Email" v={budget.client_email} />}
            {budget.client_address && <Field icon={MapPin} label="Dirección" v={budget.client_address} />}
          </div>
        </Section>

        {/* Concepts */}
        <Section label="Conceptos">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200">
                <tr className="text-left">
                  <th className="kbar py-3">Concepto</th>
                  <th className="kbar py-3 text-right">Cant.</th>
                  <th className="kbar py-3">Ud.</th>
                  <th className="kbar py-3 text-right">Precio</th>
                  <th className="kbar py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(budget.items || []).map((it, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`public-item-${i}`}>
                    <td className="py-3 pr-3">{it.concept}</td>
                    <td className="py-3 text-right tabular-nums">{it.quantity}</td>
                    <td className="py-3">{it.unit}</td>
                    <td className="py-3 text-right tabular-nums">{Number(it.unit_price).toFixed(2)} €</td>
                    <td className="py-3 text-right tabular-nums font-semibold">{Number(it.total).toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Materials info */}
        {budget.materials && (
          <Section label="Materiales especificados">
            <p className="text-sm text-slate-700">{budget.materials}</p>
          </Section>
        )}
        {!budget.materials_included && (
          <Section label="Materiales">
            <p className="text-sm text-slate-700">Los materiales serán aportados por el cliente.</p>
          </Section>
        )}

        {/* Totals */}
        <div className="bg-white border border-slate-200">
          <div className="p-6 space-y-2">
            <Row label="Subtotal" v={`${Number(budget.subtotal).toFixed(2)} €`} />
            <Row label={budget.iva_rate === 0 ? "Sin impuesto" : `${budget.tax_label || "IVA"} (${budget.iva_rate}%)`} v={`${Number(budget.iva_amount).toFixed(2)} €`} />
          </div>
          <div className="p-6 text-white flex items-center justify-between" style={{ background: brand }}>
            <span className="font-bold">TOTAL</span>
            <span className="font-display font-extrabold text-3xl tabular-nums" data-testid="public-total">{Number(budget.total).toFixed(2)} €</span>
          </div>
        </div>

        {/* Terms */}
        {budget.terms && (
          <Section label="Condiciones">
            <p className="text-sm text-slate-700 whitespace-pre-line">{budget.terms}</p>
            <p className="text-xs text-slate-400 mt-3">Validez del presupuesto: {budget.validity_days} días · Plazo de entrega estimado: {budget.delivery_days} días</p>
          </Section>
        )}

        {/* Actions */}
        {!closed && (
          <div className="bg-white border border-slate-200 p-6 sticky bottom-4">
            <div className="kbar mb-3">¿Qué desea hacer?</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button onClick={accept} disabled={busy} data-testid="public-accept" className="h-12 inline-flex items-center justify-center gap-2 font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50">
                <Check className="w-4 h-4" /> Aceptar presupuesto
              </button>
              <button onClick={() => setShowSign(true)} disabled={busy} data-testid="public-sign-btn" className="h-12 inline-flex items-center justify-center gap-2 font-bold text-white disabled:opacity-50" style={{ background: brand }}>
                <Pencil className="w-4 h-4" /> Firmar presupuesto
              </button>
              <button onClick={reject} disabled={busy} data-testid="public-reject" className="h-12 inline-flex items-center justify-center gap-2 font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">
                <X className="w-4 h-4" /> Rechazar
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-3 text-center">Al firmar, el presupuesto se considera aceptado automáticamente.</p>
          </div>
        )}
        {closed && (
          <div className="bg-slate-100 border border-slate-200 p-6 text-center">
            <p className="text-slate-700">Este presupuesto ya está {budget.status === "accepted" ? "aceptado" : "rechazado"}. Si necesita una nueva versión, contacte con {owner.business_name}.</p>
          </div>
        )}

        <footer className="text-center text-xs text-slate-400 py-6">
          Presupuesto generado con AutoPresupuestos Pro · {owner.business_name}
        </footer>
      </main>

      {showSign && <SignaturePad defaultName={budget.client_name} onSign={sign} onClose={() => setShowSign(false)} />}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="bg-white border border-slate-200 p-6">
      <div className="kbar mb-4">{label}</div>
      {children}
    </div>
  );
}
function Field({ icon: Icon, label, v }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-slate-400 mt-1" />
      <div>
        <div className="kbar">{label}</div>
        <div className="font-semibold">{v}</div>
      </div>
    </div>
  );
}
function Row({ label, v }) {
  return <div className="flex justify-between text-sm"><span className="text-slate-500">{label}</span><span className="tabular-nums">{v}</span></div>;
}
function StatusBadge({ status }) {
  const map = {
    draft: ["bg-slate-100 text-slate-700", "Borrador"],
    sent: ["bg-blue-100 text-blue-700", "Pendiente de respuesta"],
    accepted: ["bg-green-100 text-green-700", "Aceptado"],
    rejected: ["bg-red-100 text-red-700", "Rechazado"],
  };
  const [cls, lbl] = map[status] || map.draft;
  return <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 ${cls}`}>{lbl}</span>;
}
function Centered({ children }) { return <div className="min-h-screen flex items-center justify-center bg-slate-50">{children}</div>; }
